/**
 * Revenue Analytics Service
 * Tracks revenue metrics for Master Admin
 */

import { supabase } from './supabase'

/**
 * Get platform revenue analytics
 */
export const getPlatformRevenueAnalytics = async (timeRange = 30) => {
  try {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - timeRange)

    // Get all organizations with subscription data
    const { data: organizations, error: orgsError } = await supabase
      .from('organizations')
      .select('id, name, subscription_tier, created_at, subscription_start_date, subscription_end_date')

    if (orgsError) throw orgsError

    // Calculate revenue metrics
    const revenueMetrics = {
      totalOrganizations: organizations?.length || 0,
      activeSubscriptions: 0,
      subscriptionBreakdown: {
        demo: 0,
        essential: 0,
        professional: 0,
        growth: 0,
        white_label: 0,
        enterprise: 0,
      },
      revenueByTier: {
        demo: 0,
        essential: 0,
        professional: 0,
        growth: 0,
        white_label: 0,
        enterprise: 0,
      },
      newOrganizations: 0,
      churnedOrganizations: 0,
      mrr: 0, // Monthly Recurring Revenue
      arr: 0, // Annual Recurring Revenue
    }

    // Subscription tier pricing (from subscription_tiers table)
    const tierPricing = {
      demo: 0,
      essential: 49,
      professional: 149,
      growth: 299,
      white_label: 599,
      enterprise: 0, // Custom pricing
    }

    if (organizations) {
      organizations.forEach((org) => {
        let tier = org.subscription_tier || 'demo'
        // Normalize old tier names
        if (tier === 'trial') tier = 'essential'

        // Count by tier
        revenueMetrics.subscriptionBreakdown[tier] = (revenueMetrics.subscriptionBreakdown[tier] || 0) + 1

        // Check if active
        if (org.subscription_end_date && new Date(org.subscription_end_date) > new Date()) {
          revenueMetrics.activeSubscriptions++
        } else if (!org.subscription_end_date) {
          revenueMetrics.activeSubscriptions++
        }

        // Calculate revenue
        const monthlyRevenue = tierPricing[tier] || 0
        revenueMetrics.revenueByTier[tier] = (revenueMetrics.revenueByTier[tier] || 0) + monthlyRevenue

        // Check if new
        if (new Date(org.created_at) >= startDate) {
          revenueMetrics.newOrganizations++
        }

        // Check if churned (subscription ended in time range)
        if (org.subscription_end_date) {
          const endDate = new Date(org.subscription_end_date)
          if (endDate >= startDate && endDate < new Date()) {
            revenueMetrics.churnedOrganizations++
          }
        }
      })

      // Calculate MRR and ARR
      revenueMetrics.mrr = Object.values(revenueMetrics.revenueByTier).reduce((sum, val) => sum + val, 0)
      revenueMetrics.arr = revenueMetrics.mrr * 12
    }

    return { success: true, data: revenueMetrics }
  } catch (error) {
    console.error('Error getting revenue analytics:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get revenue trends over time
 */
export const getRevenueTrends = async (days = 90) => {
  try {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    const { data: organizations, error } = await supabase
      .from('organizations')
      .select('id, subscription_tier, created_at, subscription_start_date, subscription_end_date')
      .gte('created_at', startDate.toISOString())

    if (error) throw error

    // Group by month
    const monthlyRevenue = {}
    const tierPricing = {
      demo: 0,
      essential: 49,
      professional: 149,
      growth: 299,
      white_label: 599,
      enterprise: 0, // Custom pricing
    }

    if (organizations) {
      organizations.forEach((org) => {
        let tier = org.subscription_tier || 'demo'
        // Normalize old tier names
        if (tier === 'trial') tier = 'essential'
        
        const month = new Date(org.created_at).toISOString().slice(0, 7) // YYYY-MM

        if (!monthlyRevenue[month]) {
          monthlyRevenue[month] = {
            month,
            revenue: 0,
            newOrganizations: 0,
            churnedOrganizations: 0,
          }
        }

        monthlyRevenue[month].revenue += tierPricing[tier] || 0
        monthlyRevenue[month].newOrganizations++

        // Check for churn
        if (org.subscription_end_date) {
          const churnMonth = new Date(org.subscription_end_date).toISOString().slice(0, 7)
          if (churnMonth >= month && churnMonth <= new Date().toISOString().slice(0, 7)) {
            if (!monthlyRevenue[churnMonth]) {
              monthlyRevenue[churnMonth] = {
                month: churnMonth,
                revenue: 0,
                newOrganizations: 0,
                churnedOrganizations: 0,
              }
            }
            monthlyRevenue[churnMonth].churnedOrganizations++
            monthlyRevenue[churnMonth].revenue -= tierPricing[tier] || 0
          }
        }
      })
    }

    const trends = Object.values(monthlyRevenue).sort((a, b) => a.month.localeCompare(b.month))

    return { success: true, data: trends }
  } catch (error) {
    console.error('Error getting revenue trends:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get organization revenue details
 */
export const getOrganizationRevenue = async (organizationId) => {
  try {
    const { data: organization, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', organizationId)
      .single()

    if (error) throw error

    const tierPricing = {
      demo: 0,
      essential: 49,
      professional: 149,
      growth: 299,
      white_label: 599,
      enterprise: 0, // Custom pricing
    }

    let tier = organization.subscription_tier || 'demo'
    // Normalize old tier names
    if (tier === 'trial') tier = 'essential'
    
    const monthlyRevenue = tierPricing[tier] || 0

    return {
      success: true,
      data: {
        organizationId,
        subscriptionTier: tier,
        monthlyRevenue,
        annualRevenue: monthlyRevenue * 12,
        subscriptionStart: organization.subscription_start_date,
        subscriptionEnd: organization.subscription_end_date,
        isActive: !organization.subscription_end_date || new Date(organization.subscription_end_date) > new Date(),
      },
    }
  } catch (error) {
    console.error('Error getting organization revenue:', error)
    return { success: false, error: error.message }
  }
}

export default {
  getPlatformRevenueAnalytics,
  getRevenueTrends,
  getOrganizationRevenue,
}

